import { officialDataAPI, templateAPI, templateImageAPI } from '@/utils/api';
import { getImageFromIndexedDB } from '@/utils/imageStorage';
import {
  isLoadableDisplayPreviewUrl,
  toManagedCanonicalUploadsUrl,
  warmDisplayCacheFromRecords,
} from '@/utils/imageDisplayAccess';
import {
  ensureDisplayImageUrls,
  isManagedStorageUrl,
  rebaseUploadsUrl,
  resolveItemPreviewUrl,
  sanitizeManagedDisplayResult,
} from '@/utils/imageUrlUtils';
import { isCanvasPreviewImageId } from '@/image-designer/utils/exportCanvasPreview';

export { isCanvasPreviewImageId };

/** blob: 仅当前会话有效，不能作为列表/跨页预览的稳定来源 */
export function isEphemeralImagePreviewUrl(url) {
  return String(url || '').trim().startsWith('blob:');
}

/**
 * 模板记录上后端 enrich 的可展示签发预览地址。
 * 不含 blob；也不含裸 /uploads/（须经 sign-batch，否则 SignedImage 换签首帧可能闪 403）。
 */
export function pickImageTemplateSignedPreviewUrl(template = {}) {
  const candidates = [
    template.previewUrlDisplay,
    template.viewUrl,
    template.displayUrl,
    template.previewUrl,
  ];
  for (const value of candidates) {
    const s = String(value || '').trim();
    if (!s || isEphemeralImagePreviewUrl(s)) continue;
    // 裸 /uploads/ 须走 sign-batch，不能当「已签发」预览直接进列表 map
    if (s.startsWith('/uploads/') || s.startsWith('/api/uploads/')) continue;
    if (isLoadableDisplayPreviewUrl(s)) return s;
  }
  return '';
}

/**
 * 商品模板列表/卡片预览 URL：优先 inline data 与服务端签发，避免 IndexedDB blob 覆盖有效地址。
 */
export function resolveImageTemplatePreviewUrl(template = {}, previewUrlMap = {}) {
  const embedded = template?.templateData?.previewDataUrl;
  if (typeof embedded === 'string' && embedded.startsWith('data:image/')) {
    return embedded;
  }

  const signed = pickImageTemplateSignedPreviewUrl(template);
  if (signed) return signed;

  const mapped = previewUrlMap[template?.id];
  if (mapped && !isEphemeralImagePreviewUrl(mapped)) return mapped;

  return null;
}

export function hasStableImageTemplatePreviewUrl(template = {}, previewUrlMap = {}) {
  return Boolean(resolveImageTemplatePreviewUrl(template, previewUrlMap));
}

function sanitizePreviewUrlForMap(url) {
  const s = String(url || '').trim();
  if (!s || isEphemeralImagePreviewUrl(s)) return '';
  // data: 可直出；其余须已是可加载签发/公网地址，禁止裸 /uploads/ 进 map
  if (s.startsWith('data:image/')) return s;
  if (!isLoadableDisplayPreviewUrl(s)) return '';
  return s;
}

export function normalizeTemplateTimes(template) {
  const createdAt =
    template?.createdAt ||
    template?.created_at ||
    template?.createTime ||
    template?.create_time ||
    '';
  const updatedAt =
    template?.updatedAt ||
    template?.updated_at ||
    template?.updateTime ||
    template?.update_time ||
    createdAt ||
    '';
  return {
    ...template,
    createdAt,
    updatedAt,
  };
}

/** 构建商品模板列表项（去掉大字段，保留列表所需元数据） */
export function buildImageTemplateListRecord(template, extras = {}) {
  return stripImageTemplateForList(normalizeTemplateTimes({
    ...template,
    type: 'image',
    ...extras,
  }));
}

/** 合并个人与官方商品模板为列表项 */
export function mergeImageTemplateCatalogEntries(templatesRes, officialImageRes) {
  const imageTemplateById = new Map();
  Object.values(templatesRes?.templates || templatesRes || {})
    .filter((t) => t?.type === 'image')
    .forEach((t) => imageTemplateById.set(String(t.id), t));

  (officialImageRes?.templates || []).forEach((t) => {
    if (!t?.id) return;
    imageTemplateById.set(String(t.id), {
      ...imageTemplateById.get(String(t.id)),
      ...t,
      type: 'image',
      official: true,
    });
  });

  return [...imageTemplateById.values()]
    .map((t) => buildImageTemplateListRecord(t));
}

/** 拉取商品模板列表（不含表格/刊登模板），供后台静默同步 */
export async function fetchImageTemplateCatalogEntries() {
  const [templatesRes, officialImageRes] = await Promise.all([
    templateAPI.getAll({ type: 'image', pageSize: 200 }),
    officialDataAPI.listImageTemplates().catch(() => ({ templates: [] })),
  ]);
  return mergeImageTemplateCatalogEntries(templatesRes, officialImageRes);
}

/** 将保存结果转为列表预览 urlMap 条目 */
export function buildTemplatePreviewUrlMapEntry(templateId, previewUrl, previewImageId) {
  const safeUrl = sanitizePreviewUrlForMap(previewUrl);
  if (!templateId || !safeUrl) return {};
  const id = String(templateId);
  const next = { [id]: safeUrl };
  if (isCanvasPreviewImageId(previewImageId)) {
    next[templateCanvasPreviewFlagKey(id)] = '1';
  }
  return next;
}

/** 列表 urlMap 中标记该模板已使用画布 PNG 预览（非印花 CSS 叠层） */
export function templateCanvasPreviewFlagKey(templateId) {
  return `${String(templateId)}__canvas_preview__`;
}

export function isTemplateCanvasPreviewFlagged(templateId, urlMap = {}) {
  return urlMap[templateCanvasPreviewFlagKey(templateId)] === '1';
}

function findCanvasPreviewKey(store = {}) {
  return Object.keys(store).find((key) => isCanvasPreviewImageId(key)) || null;
}

/** 列表页去掉 templateData 中的大图/base64，加快接口解析与首屏渲染 */
export function stripImageTemplateForList(template) {
  if (!template || typeof template !== 'object') return template;
  const td = template.templateData && typeof template.templateData === 'object' ? template.templateData : {};
  const images = Array.isArray(td.images)
    ? td.images.map((img) => {
        if (img == null) return null;
        if (typeof img === 'string') return { id: img };
        return {
          id: img.id,
          name: img.name,
          width: img.width,
          height: img.height,
        };
      }).filter(Boolean)
    : [];

  let previewImageId = template.previewImageId;
  if (!previewImageId && images.length > 0) {
    previewImageId = images[0].id;
  }

  const stampAreas =
    td.stampAreas && typeof td.stampAreas === 'object' && !Array.isArray(td.stampAreas)
      ? td.stampAreas
      : {};

  return {
    ...template,
    previewImageId,
    workspaceMode: template.workspaceMode || template.captureSpace || '',
    scopeNo: template.scopeNo || template.scope_no || '',
    userNo: template.userNo || template.user_no || '',
    teamNo: template.teamNo || template.team_no || '',
    teamId: template.teamId ?? template.team_id ?? null,
    templateData: {
      images,
      // 主图创建创建任务依赖印花区域元数据，不能清空
      stampAreas,
      // 列表印花叠层需按工作区尺寸反算 900 contain 坐标
      workspaceWidth: td.workspaceWidth,
      workspaceHeight: td.workspaceHeight,
      primaryImageId: td.primaryImageId,
    },
  };
}

/** 从模板图片记录提取 canonical / data URL（不拼公网直链） */
export function extractTemplateImageRecordRawUrl(rec) {
  if (!rec || typeof rec !== 'object') return '';
  const dataUrl = String(rec.dataUrl || '').trim();
  const url = String(rec.url || rec.imageUrl || rec.storedUrl || '').trim();
  // 真正的 inline data URL 优先（上传失败兜底）
  if (dataUrl.startsWith('data:image/')) return dataUrl;
  // 受管路径优先读 url（后端 enrich 会对 url 签 viewUrl）
  if (url) {
    const canonical = toManagedCanonicalUploadsUrl(url);
    return canonical || url;
  }
  // 历史：受管地址写在 dataUrl
  if (dataUrl && !dataUrl.startsWith('blob:')) {
    const canonical = toManagedCanonicalUploadsUrl(dataUrl);
    return canonical || dataUrl;
  }
  // 仅有签发地址时还原 canonical，供复制/设计器持久化与 sign-batch
  for (const signed of [rec.viewUrl, rec.displayUrl, rec.urlDisplay]) {
    const canonical = toManagedCanonicalUploadsUrl(signed);
    if (canonical) return canonical;
  }
  return '';
}

function pickSlidePreviewUrl(rec) {
  const raw = extractTemplateImageRecordRawUrl(rec);
  if (!raw) return '';
  if (raw.startsWith('data:image/')) return raw;
  return sanitizeManagedDisplayResult(rebaseUploadsUrl(raw), raw);
}

/** 解析套图模板单张图片的可展示 URL（同步：legacy 公网直链，列表预取请用 loadImageTemplatePreviewUrls） */
export function resolveTemplateImageRecordUrl(rec) {
  return pickSlidePreviewUrl(rec);
}

function managedRawUrl(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return '';
  return isManagedStorageUrl(trimmed) ? trimmed : '';
}

/** 异步签名读图，供设计器 canvas / 模板 reload 使用 */
export async function resolveTemplateImageRecordUrlAsync(rec) {
  const raw = extractTemplateImageRecordRawUrl(rec);
  if (!raw) return '';
  if (raw.startsWith('data:image/')) return raw;
  const resolved = await resolveItemPreviewUrl(raw, rec);
  return sanitizeManagedDisplayResult(resolved, raw);
}

async function resolvePreviewUrlAfterBatch(record) {
  const raw = extractTemplateImageRecordRawUrl(record);
  if (!raw) return '';
  if (raw.startsWith('data:image/')) return raw;
  return resolveTemplateImageRecordUrlAsync(record);
}

/**
 * 编辑器 urlMap：批量 sign-batch 后解析可展示 URL，避免中间态裸 /uploads/ 链。
 * @param {Array<{ id: string, record?: object }>} entries
 * @returns {Promise<Record<string, string>>}
 */
export async function resolveTemplateImageUrlMap(entries) {
  const urlMap = {};
  const pending = [];
  const list = Array.isArray(entries) ? entries : [];

  for (const entry of list) {
    const id = String(entry?.id || '').trim();
    const record = entry?.record;
    if (!id || !record) continue;
    const raw = extractTemplateImageRecordRawUrl(record);
    if (!raw) continue;
    if (raw.startsWith('data:')) {
      urlMap[id] = raw;
      continue;
    }
    if (raw.startsWith('blob:')) {
      continue;
    }
    pending.push({ id, record });
  }

  const managedUrls = [
    ...new Set(
      pending
        .map(({ record }) => managedRawUrl(extractTemplateImageRecordRawUrl(record)))
        .filter(Boolean),
    ),
  ];
  if (managedUrls.length > 0) {
    await ensureDisplayImageUrls(managedUrls);
  }

  await Promise.all(
    pending.map(async ({ id, record }) => {
      const previewUrl = await resolvePreviewUrlAfterBatch(record);
      if (previewUrl) urlMap[id] = previewUrl;
    }),
  );

  return urlMap;
}

function collectSlideThumbSources(template, store = {}) {
  const MAX_THUMBS = 3;
  const slides = [];
  const seen = new Set();

  const push = (imageId, record) => {
    const id = String(imageId || '').trim();
    if (!id || seen.has(id) || isCanvasPreviewImageId(id)) return;
    if (!record || typeof record !== 'object') return;
    const raw = extractTemplateImageRecordRawUrl(record);
    if (!raw || isEphemeralImagePreviewUrl(raw)) return;
    seen.add(id);
    slides.push({ imageId: id, record });
  };

  const metaImages = Array.isArray(template?.templateData?.images)
    ? template.templateData.images
    : [];
  for (const img of metaImages) {
    if (slides.length >= MAX_THUMBS) break;
    const id = img?.id;
    if (!id) continue;
    // 优先 template-images 表记录（含后端 enrich 的 viewUrl），否则用 templateData 内联字段
    push(id, store[id] || img);
  }

  if (slides.length < MAX_THUMBS) {
    for (const key of Object.keys(store)) {
      if (slides.length >= MAX_THUMBS) break;
      push(key, store[key]);
    }
  }

  return slides;
}

/**
 * 解析单条模板预览来源（不签名），供列表批量预取使用。
 * @param {object} template
 * @param {{ preferBaseSlide?: boolean, includeSlideThumbs?: boolean }} [options]
 *   preferBaseSlide：弹窗选型时优先商品底图，避免画布合成预览上的 ipelayer 占位框
 *   includeSlideThumbs：额外解析轮播图（按 imageId 写入 urlMap，供 GoodsTemplateGrid 多缩略图）
 * @returns {Promise<{ templateId: string, flagKey: string, embeddedUrl?: string, record?: object, canvasPreview?: boolean, slides?: Array<{ imageId: string, record: object }> } | null>}
 */
async function resolveTemplatePreviewSource(template, options = {}) {
  const preferBaseSlide = options.preferBaseSlide === true;
  const includeSlideThumbs = options.includeSlideThumbs === true;
  const templateId = String(template.id);
  const flagKey = templateCanvasPreviewFlagKey(templateId);

  const embedded = template?.templateData?.previewDataUrl;
  const hasEmbedded =
    typeof embedded === 'string'
    && embedded.startsWith('data:image/')
    && embedded.length < 800000;

  // 无多缩略图需求且已有合成预览时，可跳过 getByTemplate（保持列表轻量）
  if (!includeSlideThumbs && !preferBaseSlide && hasEmbedded) {
    return { templateId, flagKey, embeddedUrl: embedded, canvasPreview: true, slides: [] };
  }

  // 列表已 enrich 签发预览：直接用，避免每条模板再打 getByTemplate / getById
  const signedPreview = pickImageTemplateSignedPreviewUrl(template);
  if (!includeSlideThumbs && !preferBaseSlide && signedPreview) {
    return {
      templateId,
      flagKey,
      embeddedUrl: signedPreview,
      canvasPreview: isCanvasPreviewImageId(template.previewImageId),
      slides: [],
    };
  }

  // 仅有受管 previewUrl（未签发）：走批量 sign，仍不打 getByTemplate
  if (!includeSlideThumbs && !preferBaseSlide) {
    const rawPreview = String(template.previewUrl || '').trim();
    const canonical = toManagedCanonicalUploadsUrl(rawPreview) || '';
    if (canonical.startsWith('/uploads/') || isManagedStorageUrl(rawPreview)) {
      return {
        templateId,
        flagKey,
        record: { url: canonical || rawPreview },
        canvasPreview: isCanvasPreviewImageId(template.previewImageId),
        slides: [],
      };
    }
  }

  let store = {};
  try {
    const res = await templateImageAPI.getByTemplate(templateId);
    store = res?.images && typeof res.images === 'object' ? res.images : {};
  } catch {
    /* ignore */
  }

  const slides = includeSlideThumbs ? collectSlideThumbSources(template, store) : [];

  const withSlides = (payload) => (payload ? { ...payload, slides } : null);

  let imageId = template.previewImageId;
  if (!imageId && Array.isArray(template?.templateData?.images) && template.templateData.images[0]?.id) {
    imageId = template.templateData.images[0].id;
  }

  const tryLocalOrRecord = async (key, { canvasPreview = false } = {}) => {
    if (!key) return null;
    if (store[key]) {
      return { templateId, flagKey, record: store[key], canvasPreview };
    }
    try {
      const local = await getImageFromIndexedDB(key);
      // 列表预览不要用 blob Object URL（刷新即失效，还会盖住服务端签发地址）
      if (!local || isEphemeralImagePreviewUrl(local)) return null;
      if (String(local).startsWith('data:image/')) {
        return { templateId, flagKey, embeddedUrl: local, canvasPreview };
      }
      return {
        templateId,
        flagKey,
        canvasPreview,
        record: { url: local },
      };
    } catch {
      /* ignore */
    }
    return null;
  };

  // 主预览仍可优先用合成图（多缩略图走 slides）
  if (!preferBaseSlide && hasEmbedded) {
    return withSlides({ templateId, flagKey, embeddedUrl: embedded, canvasPreview: true });
  }

  // 优先非画布底图（轮播首图），避免弹出选型看到 ipelayer 白框标签
  if (preferBaseSlide) {
    const baseKey = imageId && !isCanvasPreviewImageId(imageId)
      ? imageId
      : Object.keys(store).find((key) => !isCanvasPreviewImageId(key));
    const baseHit = await tryLocalOrRecord(baseKey, { canvasPreview: false });
    if (baseHit) return withSlides(baseHit);
  }

  if (!preferBaseSlide) {
    const canvasPreviewKey = findCanvasPreviewKey(store);
    if (canvasPreviewKey && store[canvasPreviewKey]) {
      return withSlides({ templateId, flagKey, record: store[canvasPreviewKey], canvasPreview: true });
    }

    if (imageId && isCanvasPreviewImageId(imageId) && store[imageId]) {
      return withSlides({ templateId, flagKey, record: store[imageId], canvasPreview: true });
    }
  }

  if (imageId) {
    const hit = await tryLocalOrRecord(imageId, {
      canvasPreview: isCanvasPreviewImageId(imageId),
    });
    if (hit) return withSlides(hit);
  }

  const preferredKey = imageId && store[imageId]
    ? imageId
    : Object.keys(store).find((key) => !preferBaseSlide || !isCanvasPreviewImageId(key))
      || Object.keys(store)[0];
  if (preferredKey && store[preferredKey]) {
    return withSlides({
      templateId,
      flagKey,
      record: store[preferredKey],
      canvasPreview: isCanvasPreviewImageId(preferredKey),
    });
  }

  // 底图都没有时，退回画布预览 / embedded（好过无预览）
  if (preferBaseSlide) {
    if (hasEmbedded) {
      return withSlides({ templateId, flagKey, embeddedUrl: embedded, canvasPreview: true });
    }
    const canvasPreviewKey = findCanvasPreviewKey(store);
    if (canvasPreviewKey && store[canvasPreviewKey]) {
      return withSlides({ templateId, flagKey, record: store[canvasPreviewKey], canvasPreview: true });
    }
  }

  // 仅有多缩略图、无主预览时也要返回，避免 slide 映射丢失
  if (slides.length > 0) {
    return { templateId, flagKey, slides };
  }

  // 列表 strip 掉 previewDataUrl、图库又无图时：getById 恢复合成预览（旧路径，仅无预览时触发）
  if (!hasEmbedded) {
    try {
      const { template: detail } = await templateAPI.getById(templateId);
      const detailPreview = detail?.templateData?.previewDataUrl;
      if (
        typeof detailPreview === 'string'
        && detailPreview.startsWith('data:image/')
        && detailPreview.length < 800000
      ) {
        return withSlides({
          templateId,
          flagKey,
          embeddedUrl: detailPreview,
          canvasPreview: true,
        });
      }
      const signedFromDetail = pickImageTemplateSignedPreviewUrl(detail || {});
      if (signedFromDetail && !isEphemeralImagePreviewUrl(signedFromDetail)) {
        return withSlides({
          templateId,
          flagKey,
          embeddedUrl: signedFromDetail,
          canvasPreview: false,
        });
      }
    } catch {
      /* ignore */
    }
  }

  return null;
}

function collectManagedRawUrls(sources) {
  const urls = [];
  for (const source of sources) {
    if (!source) continue;
    if (source.embeddedUrl) {
      const signed = managedRawUrl(source.embeddedUrl);
      if (signed) urls.push(signed);
    }
    if (source.record) {
      const signed = managedRawUrl(extractTemplateImageRecordRawUrl(source.record));
      if (signed) urls.push(signed);
    }
    for (const slide of source.slides || []) {
      if (!slide?.record) continue;
      const signed = managedRawUrl(extractTemplateImageRecordRawUrl(slide.record));
      if (signed) urls.push(signed);
    }
  }
  return urls;
}

/**
 * 仅为当前页模板加载预览；有列表预览/图库图时不调用 getById，仅无图时兜底 getById。
 * 先批量 ensureDisplayImageUrls，再 resolveTemplateImageRecordUrlAsync，避免首帧 403 与重复 sign。
 * @param {object[]} templates
 * @param {{ concurrency?: number, preferBaseSlide?: boolean, includeSlideThumbs?: boolean }} [options]
 *   includeSlideThumbs：同时按 slide imageId 写入 urlMap（GoodsTemplateGrid 多缩略图依赖）
 */
export async function loadImageTemplatePreviewUrls(
  templates,
  { concurrency = 4, preferBaseSlide = false, includeSlideThumbs = false } = {},
) {
  const urlMap = {};
  const list = Array.isArray(templates) ? templates.filter((t) => t?.id) : [];
  if (list.length === 0) return urlMap;

  // 先吃掉已有签发预览，减少后续 N+1
  const needResolve = [];
  for (const template of list) {
    if (!includeSlideThumbs && !preferBaseSlide) {
      const signed = pickImageTemplateSignedPreviewUrl(template);
      if (signed) {
        const safe = sanitizePreviewUrlForMap(signed);
        if (safe) {
          urlMap[String(template.id)] = safe;
          if (isCanvasPreviewImageId(template.previewImageId)) {
            urlMap[templateCanvasPreviewFlagKey(template.id)] = '1';
          }
          continue;
        }
      }
    }
    needResolve.push(template);
  }

  const sources = [];
  for (let i = 0; i < needResolve.length; i += concurrency) {
    const batch = needResolve.slice(i, concurrency + i);
    const resolved = await Promise.all(
      batch.map((template) => resolveTemplatePreviewSource(template, {
        preferBaseSlide,
        includeSlideThumbs,
      })),
    );
    sources.push(...resolved.filter(Boolean));
  }

  const managedUrls = [...new Set(collectManagedRawUrls(sources))];
  if (managedUrls.length > 0) {
    await ensureDisplayImageUrls(managedUrls);
  }

  await Promise.all(sources.map(async (source) => {
    const { templateId, flagKey, embeddedUrl, record, canvasPreview, slides } = source;

    if (embeddedUrl) {
      const safeEmbedded = sanitizePreviewUrlForMap(embeddedUrl);
      if (safeEmbedded) {
        urlMap[templateId] = safeEmbedded;
        if (canvasPreview) urlMap[flagKey] = '1';
      }
    } else if (record) {
      const previewUrl = sanitizePreviewUrlForMap(await resolvePreviewUrlAfterBatch(record));
      if (previewUrl) {
        urlMap[templateId] = previewUrl;
        if (canvasPreview) urlMap[flagKey] = '1';
      }
    }

    // 多缩略图：按 slide imageId 写入，供 GoodsTemplateGrid.thumbEntries 读取
    for (const slide of slides || []) {
      const imageId = String(slide?.imageId || '').trim();
      if (!imageId || !slide.record || urlMap[imageId]) continue;
      const slideUrl = sanitizePreviewUrlForMap(await resolvePreviewUrlAfterBatch(slide.record));
      if (slideUrl) urlMap[imageId] = slideUrl;
    }
  }));

  // 把已签发预览写入展示缓存（含 HMAC thumb 派生），列表 SignedImage(variant=thumb) 可同步命中，
  // 避免每卡再走 sign-batch（线下归属失败 / 线上默认域名直链都会导致封面空白）。
  const warmItems = [];
  for (const [id, preview] of Object.entries(urlMap)) {
    if (String(id).includes('__canvas_preview__')) continue;
    const s = String(preview || '').trim();
    if (!s || !isLoadableDisplayPreviewUrl(s)) continue;
    const canonical = toManagedCanonicalUploadsUrl(s) || s;
    warmItems.push({
      id,
      url: canonical,
      viewUrl: s,
      displayUrl: s,
    });
  }
  if (warmItems.length > 0) {
    warmDisplayCacheFromRecords(warmItems);
  }

  return urlMap;
}
